import React, { useState, useEffect } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import {
  LayoutDashboard,
  Package,
  Truck,
  ArrowDownToLine,
  ArrowUpFromLine,
  Bell,
  BarChart3,
  Users,
  ScrollText,
  Trash2,
  Settings,
  LogOut,
  Menu,
  X,
} from "lucide-react";
import { Button } from "../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import { Badge } from "../components/ui/badge";

const MainLayout = () => {
  const { user, company, logout } = useAuth();  // MULTI-TENANT: Adicionar company
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notificationCount] = useState(0);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  // Auto-download daily backup
  useEffect(() => {
    const autoBackupDownload = async () => {
      try {
        const today = new Date().toISOString().split("T")[0];
        const lastDownload = localStorage.getItem("last_backup_download");
        if (lastDownload === today) return;

        const response = await api.get("/backup/export/json", { responseType: "blob" });
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `backup_estoqueclinic_${today}.json`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        localStorage.setItem("last_backup_download", today);
      } catch (error) {
        // Silent fail - don't block user experience
      }
    };

    const timer = setTimeout(autoBackupDownload, 3000);
    return () => clearTimeout(timer);
  }, []);

  const navItems = [
    { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
    { to: "/products", icon: Package, label: "Produtos" },
    { to: "/suppliers", icon: Truck, label: "Fornecedores" },
    { to: "/stock-entry", icon: ArrowDownToLine, label: "Entrada de Estoque" },
    { to: "/stock-exit", icon: ArrowUpFromLine, label: "Saída de Estoque" },
    { to: "/notifications", icon: Bell, label: "Notificações", badge: notificationCount },
    { to: "/reports", icon: BarChart3, label: "Relatórios" },
  ];

  const adminItems = [
    { to: "/team", icon: Users, label: "Equipe" },
    { to: "/system-settings", icon: Settings, label: "Configurações" },
    { to: "/audit-log", icon: ScrollText, label: "Log de Auditoria" },
    { to: "/trash", icon: Trash2, label: "Lixeira" },
  ];

  const NavItem = ({ item }) => (
    <NavLink
      to={item.to}
      onClick={() => setSidebarOpen(false)}
      className={({ isActive }) =>
        `sidebar-item ${isActive ? "sidebar-item-active" : ""}`
      }
      data-testid={`nav-${item.to.replace("/", "")}`}
    >
      <item.icon className="h-5 w-5" />
      <span>{item.label}</span>
      {item.badge > 0 && (
        <Badge variant="destructive" className="ml-auto text-xs">
          {item.badge}
        </Badge>
      )}
    </NavLink>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-white border-b border-slate-200 z-50 flex items-center justify-between px-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          data-testid="mobile-menu-toggle"
        >
          {sidebarOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </Button>
        <h1 className="font-heading font-bold text-lg text-primary">EstoqueClinic</h1>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" data-testid="user-menu-mobile">
              <Avatar className="h-8 w-8">
                <AvatarFallback className="bg-primary text-white text-sm">
                  {user?.name?.charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => navigate("/settings")}>
              <Settings className="mr-2 h-4 w-4" />
              Configurações
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout}>
              <LogOut className="mr-2 h-4 w-4" />
              Sair
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </header>

      {/* Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 h-full w-64 bg-white border-r border-slate-200 z-50 transform transition-transform duration-200 lg:translate-x-0 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo + Company Name */}
          <div className="h-auto flex flex-col items-start px-6 py-4 border-b border-slate-100">
            <h1 className="font-heading font-bold text-xl text-primary">EstoqueClinic</h1>
            {company && (
              <span className="text-xs text-slate-500 mt-1 truncate max-w-full" title={company.name}>
                {company.name}
              </span>
            )}
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto py-4 px-3">
            <div className="space-y-1">
              {navItems.map((item) => (
                <NavItem key={item.to} item={item} />
              ))}
            </div>

            {user?.role === "admin" && (
              <>
                <div className="mt-6 mb-2 px-4">
                  <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                    Administração
                  </span>
                </div>
                <div className="space-y-1">
                  {adminItems.map((item) => (
                    <NavItem key={item.to} item={item} />
                  ))}
                </div>
              </>
            )}
          </nav>

          {/* User Section */}
          <div className="p-4 border-t border-slate-100">
            {/* User Info */}
            <div className="flex items-center gap-3 mb-3">
              <Avatar className="h-9 w-9">
                <AvatarFallback className="bg-primary text-white">
                  {user?.name?.charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900 truncate">
                  {user?.name}
                </p>
                <p className="text-xs text-slate-500 capitalize">{user?.role}</p>
              </div>
            </div>
            
            {/* Action Buttons */}
            <div className="space-y-1">
              <Button
                variant="ghost"
                className="w-full justify-start gap-2 h-9 text-slate-600 hover:text-slate-900"
                onClick={() => navigate("/settings")}
              >
                <Settings className="h-4 w-4" />
                Gerenciamento de Conta
              </Button>
              <Button
                variant="ghost"
                className="w-full justify-start gap-2 h-9 text-red-600 hover:text-red-700 hover:bg-red-50"
                onClick={handleLogout}
                data-testid="logout-btn"
              >
                <LogOut className="h-4 w-4" />
                Sair
              </Button>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:pl-64 pt-16 lg:pt-0 min-h-screen">
        <div className="p-6 lg:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default MainLayout;
